/**
 * Mapuje techniczne błędy z bazy (Postgres / Supabase) na czytelne komunikaty po polsku.
 *
 * Wytyczne:
 * - Pisz krótko i konkretnie - co użytkownik ma zrobić.
 * - Używaj krótkich myślników "-", nigdy "—" ani "–".
 * - Nie pokazuj nazw tabel, kolumn ani constraintów technicznych.
 * - Domyślny komunikat ma sens dla użytkownika niebędącego programistą.
 */

type ConstraintMap = Record<string, string>;

/**
 * Mapowanie nazw constraintów z Postgresa na komunikaty UI.
 * Klucz = nazwa constraintu (z `pg_constraint.conname`).
 */
const CONSTRAINT_MESSAGES: ConstraintMap = {
  // Format / długość pól kontaktowych
  shelters_email_format: "Podaj poprawny adres e-mail (np. kontakt@schronisko.pl).",
  shelters_email_length: "Adres e-mail jest za długi (maksymalnie 255 znaków).",
  shelters_phone_format:
    "Podaj poprawny numer telefonu - dozwolone są cyfry oraz znaki + - ( ) i spacje.",
  shelters_phone_length: "Numer telefonu jest za długi (maksymalnie 100 znaków).",
  shelters_website_length: "Adres strony WWW jest za długi (maksymalnie 500 znaków).",
  shelters_website_safe_scheme:
    "Adres strony WWW musi zaczynać się od http:// lub https://.",

  // Długości pól tekstowych
  shelters_name_length: "Nazwa schroniska musi mieć od 1 do 200 znaków.",
  shelters_city_length: "Nazwa miasta musi mieć od 1 do 100 znaków.",
  shelters_address_length: "Adres jest za długi (maksymalnie 500 znaków).",
  shelters_postal_code_length: "Kod pocztowy jest za długi (maksymalnie 20 znaków).",
  shelters_region_length: "Nazwa województwa jest za długa (maksymalnie 100 znaków).",
  shelters_current_needs_len:
    "Opis potrzeb jest za długi - skróć go do 2000 znaków.",
  shelters_volunteer_info_len:
    "Opis wolontariatu jest za długi - skróć go do 2000 znaków.",
  shelters_description_len:
    "Opis schroniska jest za długi - skróć go do 5000 znaków.",

  // Lokalizacja
  shelters_lat_bounds: "Nieprawidłowa lokalizacja - wybierz punkt na mapie ponownie.",
  shelters_lng_bounds: "Nieprawidłowa lokalizacja - wybierz punkt na mapie ponownie.",

  // Stan weryfikacji
  shelters_verified_requires_verified_at:
    "Nie udało się zaktualizować stanu weryfikacji. Skontaktuj się z administratorem.",
};

/**
 * Generyczne mapowanie kodów błędów Postgresa.
 */
const PG_CODE_MESSAGES: Record<string, string> = {
  "23505": "Taki wpis już istnieje.",
  "23502": "Brakuje wymaganego pola - uzupełnij wszystkie obowiązkowe dane.",
  "23503": "Operacja nie jest możliwa - powiązane dane nie istnieją.",
  "22001": "Wprowadzony tekst jest za długi.",
  "22P02": "Nieprawidłowy format danych.",
  "42501": "Brak uprawnień do wykonania tej operacji.",
};

const FRIENDLY_FALLBACK = "Nie udało się zapisać zmian. Spróbuj ponownie za chwilę.";

/**
 * Wyciąga przyjazny komunikat z błędu Supabase / Postgres.
 */
export function dbErrorMessage(err: unknown, fallback?: string): string {
  const fb = fallback ?? FRIENDLY_FALLBACK;
  if (!err) return fb;

  // Supabase PostgrestError: { message, details, hint, code }
  const e = err as Record<string, unknown>;
  const message = typeof e.message === "string" ? e.message : "";
  const details = typeof e.details === "string" ? e.details : "";
  const hint = typeof e.hint === "string" ? e.hint : "";
  const code = typeof e.code === "string" ? e.code : "";
  const haystack = `${message} ${details} ${hint}`;

  // 1) Próba wykrycia nazwy constraintu w treści błędu
  const constraintMatch = haystack.match(
    /violates (?:check|unique|foreign key|not-null) constraint "([^"]+)"/i,
  );
  if (constraintMatch) {
    const name = constraintMatch[1];
    if (CONSTRAINT_MESSAGES[name]) return CONSTRAINT_MESSAGES[name];
  }

  // 2) Mapowanie po kodzie SQLSTATE
  if (code && PG_CODE_MESSAGES[code]) return PG_CODE_MESSAGES[code];

  // 3) Heurystyki na surowym tekście (na wypadek gdyby treść nie zawierała constraintu)
  const lower = haystack.toLowerCase();
  if (lower.includes("email")) {
    return "Podaj poprawny adres e-mail (np. kontakt@schronisko.pl).";
  }
  if (lower.includes("phone")) {
    return "Podaj poprawny numer telefonu - dozwolone są cyfry oraz znaki + - ( ) i spacje.";
  }
  if (lower.includes("website") || lower.includes("url")) {
    return "Adres strony WWW musi zaczynać się od http:// lub https://.";
  }

  // 4) Komunikat techniczny - ukrywamy go i dajemy fallback
  return fb;
}
